// eslint-disable-next-line @typescript-eslint/no-explicit-any
let config: { modules?: Record<string, any> } = {};

export const parseKitchenSink = response => {
  const {
    version,
    menuId,
    config: ksConfig,
    order,
    rows,
    autoplay,
    autoplayObject,
    providers,
    networks,
    settings,
    globalSettings,
    playback,
    hero,
    menu,
    tag_rows: tagRows,
  } = response.page;

  config = parseConfig(ksConfig);

  return {
    version,
    menuId,
    menu: parseMenu(menu),
    order: parseOrder(order, rows),
    config: parseConfig(ksConfig),
    rows: parseRows(order, rows),
    autoplay,
    autoplayObject: parseVideo(autoplayObject),
    providers: parseProviders(providers),
    networks: parseNetworks(networks),
    settings: parseSettings(settings),
    globalSettings: parseGlobalSettings(globalSettings),
    playback: parsePlayback(playback),
    hero: parseHero(hero),
    sports: parseSports(tagRows),
    leagues: parseLeagues(tagRows),
    allsports: parseAllSports(tagRows),
    localMarkets: parseLocalMarkets(tagRows),
    shows: parseShows(tagRows),
  };
};

export const parseKitchenSinkRows = response => {
  const { rows } = response.page;
  return Object.keys(rows).reduce((accumulator, rowId) => {
    const parsed = parseRowData(rows[rowId]);
    if (parsed && parsed.length) {
      accumulator[rowId] = parsed;
    }
    return accumulator;
  }, {});
};

export const parseKitchenSinkHero = response => {
  const { hero } = response.page;

  if (!hero) return {};

  const { properties, data } = hero;
  const heroData = data.map(item => {
    return parseVideo(item);
  });

  return { properties, data: heroData };
};

const parseConfigModule = module => {
  const params = {};

  for (const { name, value } of module.params) {
    if (Object.prototype.hasOwnProperty.call(params, name)) {
      // Convert to array or push to existing array
      if (Array.isArray(params[name])) {
        params[name].push(value);
      } else {
        params[name] = [params[name], value];
      }
    } else {
      params[name] = value;
    }
  }

  return {
    params,
    category: module.category,
    enabled: module.enabled,
  };
};

const parseConfig = ksConfig => {
  return {
    modules: ksConfig?.modules?.reduce((accumulator, module) => {
      accumulator[module.name] = parseConfigModule(module);
      return accumulator;
    }, {}),
    classes: ksConfig?.classes,
  };
};

export const parseVideo = item => {
  const { id, order, featured, dmaStation, video } = item;
  const {
    about = {},
    ads = {},
    authenticationDetails = {},
    authentication = {},
    event = {},
    flags = {},
    identifiers = {},
    network = null,
    playback,
    properties = {},
    schedule = {},
    sources = {},
  } = video;
  return {
    id,
    dmaStation,
    trackingId: identifiers.trackingId,
    viacomContentId: identifiers.viacomContentId,
    type: 'VIDEO',
    order,
    isFeatured: featured,
    network,
    about: {
      title: about.title,
      shortTitle: about.shortTitle,
      segmentTitle: about.segmentTitle,
      duration: about.duration,
      description: about.description,
      publishedOn: about.publishedOn,
      prefix: about.prefix,
    },
    ads: {
      isDai: flags.useDai,
      daiIu: ads.dai?.daiIu,
      daiAssetKey: ads.dai?.daiAssetKey,
      daiApiKey: ads.dai?.daiApiKey,
      daiOt: ads.dai?.daiOt,
      daiOv: ads.dai?.daiOv,
      daiApto: ads.dai?.daiApto,
      daiCsid: ads.dai?.daiCsid,
    },
    authenticationDetails,
    authTypes: authentication,
    event: {
      status: event.status,
      preEvent: event.preEvent,
      postEvent: event.postEvent,
      pregame: parsePregame(event.pregame, authentication),
    },
    images: about.images && {
      baseImage16X9: about.images.baseImage16X9,
      baseImage16X5: about.images.baseImage16X5,
    },
    playback: {
      isSyncbak: playback === PlaybackTypes.SYNCBAK,
      url:
        playback === PlaybackTypes.FULL_DRM
          ? sources.dash.url
          : sources.hls.url,
      type: playback,
    },
    properties: {
      type: properties.type,
      gameId: properties.gameId,
      league: properties.league,
    },
    schedule: {
      preStartDate: schedule.preStartDate,
      preEndDate: schedule.preEndDate,
      videoStartDate: schedule.videoStartDate,
      videoEndDate: schedule.videoEndDate,
      postStartDate: schedule.postStartDate,
      postEndDate: schedule.postEndDate,
      apiStartDate: schedule.apiStartDate,
      apiEndDate: schedule.apiEndDate,
    },
  };
};

const parsePregame = (item, authentication) => {
  if (item == null) return null;
  return {
    trackingId: item.identifiers.trackingId,
    viacomContentId: item.viacomContentId,
    network: item.network,
    about: {
      title: item.title,
    },
    ads: {
      isDai: item.useDai,
      daiIu: item.dai?.daiIu,
      daiAssetKey: item.dai?.daiAssetKey,
      daiApiKey: item.dai?.daiApiKey,
      daiOt: item.dai?.daiOt,
      daiOv: item.dai?.daiOv,
      daiApto: item.dai?.daiApto,
    },
    images: item.thumbnail,
    playback: {
      isSyncbak: item.playback === PlaybackTypes.SYNCBAK,
      url:
        item.playback === PlaybackTypes.FULL_DRM
          ? item.sources.dash.url
          : item.sources.hls.url,
      type: item.playback,
    },
    authTypes: authentication,
  };
};

const parsePromo = (id, promo) => {
  return {
    id,
    type: 'PROMO',
    title: promo.title,
    images: promo.images && {
      baseImage16X9: promo.images.baseImage16X9,
      baseImage16X5: promo.images.baseImage16X5,
    },
    tagId: promo.tag?.id,
    tagKey: promo.tag?.key,
    tagType: promo.tag?.type?.name,
  };
};

export const parseRowData = rowData => {
  const auth = config?.modules?.Auth?.params;

  if (!Array.isArray(rowData)) {
    return null;
  }

  return rowData
    .map(item => {
      if (item.promo) {
        return parsePromo(item.id, item.promo);
      }

      if (item.video) {
        const { authentication } = item.video;
        if (auth?.shortCode_PPlus === '0' || auth?.shortCode_MVPD === '0') {
          if (
            authentication.includes('paramountplus') ||
            authentication.includes('adobe')
          ) {
            return null;
          }
        }
        return parseVideo(item);
      }

      return null;
    })
    .filter(item => item !== null);
};

const parseRows = (order, rows) => {
  const getLayoutType = layout => {
    switch (layout) {
      case 'oneacross':
        return 1;
      case 'twoacross':
        return 2;
      case 'threeacross':
      case 'threeAcrossHighlight':
        return 3;
      case 'fouracross':
      case 'fourAcrossHighlight':
        return 4;
      case 'fiveacross':
      default:
        return 5;
    }
  };

  return order
    .filter(({ id }) => rows[id])
    .map(({ id, type, slug, title: rowTitle, description, url, layout }) => {
      const row = rows[id];
      const parsed = parseRowData(row);

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return null;
      }

      return {
        data: parsed,
        id,
        type,
        slug,
        title: rowTitle,
        description,
        url,
        layoutType: getLayoutType(layout),
      };
    })
    .filter(item => item !== null);
};

const parseProviders = providers => {
  const { adobe, paramountplus } = providers;

  const adobeResult = {
    secondScreenUrl: adobe.secondScreenUrl,
    authUrl: adobe.authUrl,
    key: adobe.authPublicKey,
    email: adobe.authEmail,
  };

  const paramountplusResult = {
    secondScreenUrl: paramountplus.secondScreenUrl,
    clientId: paramountplus.clientId,
    clientSecret: paramountplus.clientSecret,
    // Checking for different logo keys because response returns inconsistent casing
    logoSmallUrl: paramountplus.logoUrlsmall || paramountplus.logoUrlSmall,
    logoMediumUrl: paramountplus.logoUrlmedium || paramountplus.logoUrlMedium,
    logoLargeUrl: paramountplus.logoUrllarge || paramountplus.logoUrlLarge,
    email: paramountplus.authEmail,
    displayName: paramountplus.displayName,
    syncbakMvpdId: paramountplus.mvpdId,
    premiumUpgradeMessage: paramountplus.premiumUpgradeMessage,
  };

  return {
    adobe: adobeResult,
    paramountplus: paramountplusResult,
  };
};

const parseNetworks = networks => {
  const result = {};
  Object.keys(networks).forEach(networkId => {
    const data = networks[networkId];
    result[networkId] = {
      resourceId: data.resource,
      name: data.name,
      logoSmallUrl: data.logoUrlSmall,
      logoMediumUrl: data.logoUrlMedium,
      logoLargeUrl: data.logoUrlLarge,
      logoFixedSmallUrl: data.logoUrlFixedSizeSmall,
      logoFixedMediumUrl: data.logoUrlFixedSizeMedium,
      logoFixedLargeUrl: data.logoUrlFixedSizeLarge,
    };
  });

  return result;
};

const parseSettings = settings => {
  try {
    return {
      legal: {
        label: settings.legal.label,
        enabled: settings.legal.enabled,
        link: settings.legal.link,
      },
    };
  } catch {
    return {};
  }
};

const parseGlobalSettings = settings => {
  return {
    max429RetryMs: settings.max429Length * 1000,
  };
};

const parsePlayback = playback => {
  const SYNCBAK_V3 = 'syncbakV3';
  return {
    [SYNCBAK_V3]: {
      host: playback[SYNCBAK_V3].playbackHost,
      key: playback[SYNCBAK_V3].playbackKey,
    },
    [PlaybackTypes.HLS_ENCRYPTION]: {
      tokenUrl: playback[PlaybackTypes.HLS_ENCRYPTION].tokenUrl,
    },
    [PlaybackTypes.FULL_DRM]: {
      tokenUrl: playback[PlaybackTypes.FULL_DRM].tokenUrl,
      widevine: playback[PlaybackTypes.FULL_DRM].widevine?.license,
      playready: playback[PlaybackTypes.FULL_DRM].playready?.license,
      fairplay: {
        licenseUrl: playback[PlaybackTypes.FULL_DRM].fairplay?.license,
        appCertUrl: playback[PlaybackTypes.FULL_DRM].fairplay?.cert,
      },
    },
    [PlaybackTypes.STATION_OPT_IN]: {
      tokenUrl: playback[PlaybackTypes.STATION_OPT_IN]?.tokenUrl,
      playbackMatrix: {
        adobe: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.adobe,
        both: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.both,
        none: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.none,
        paramountplus:
          playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.paramountplus,
      },
    },
  };
};

const parseHero = hero => {
  const { properties, data } = hero;
  const heroData = data.map(item => {
    return parseVideo(item);
  });

  return { properties, data: heroData };
};

const parseSports = tagRows => {
  const { sports } = tagRows || {};
  if (!sports) {
    return sports;
  }
  return sports.filter(item => item?.tag?.isActive);
};

const parseAllSports = tagRows => {
  const { sports } = tagRows || {};

  if (!sports) {
    return sports;
  }
  return sports
    .map(row => {
      const parsed = row?.leagues;

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return null;
      }

      return {
        data: parsed,
        id: `${row.name.toLowerCase()}-${row.id}`,
        type: 'sportsAndLeagues',
        slug: row.tag.key,
        title: row.displayName,
        description: row.description,
        url: 'www.cbsnews.com',
        layoutType: 'sportsAndLeagues',
        isActive: row.tag.isActive,
      };
    })
    .filter(item => item !== null && item.isActive);
};

const parseLeagues = tagRows => {
  const { leagues } = tagRows || {};
  if (!leagues) {
    return leagues;
  }
  return leagues.filter(item => item?.tag?.isActive);
};

const parseLocalMarkets = tagRows => {
  const { markets } = tagRows || {};
  if (!markets) {
    return markets;
  }
  return markets.filter(item => item?.tag?.isActive);
};

const parseShows = tagRows => {
  const { shows } = tagRows || {};
  if (!shows) {
    return shows;
  }
  return shows.filter(item => item?.tag?.isActive);
};

export const PlaybackTypes = {
  SYNCBAK: 'syncbak',
  FULL_DRM: 'fullDRM',
  OPEN_HLS: 'openHLS',
  HLS_ENCRYPTION: 'hls_encryption',
  STATION_OPT_IN: 'stationOptIn',
};

const parseOrder = (order, rows) => {
  return order.filter(
    ({ id, type }) =>
      rows[id] || type === 'sportsAndLeagues' || type === 'tagRow',
  );
};

const parseMenu = menu => {
  const maxTags = 6;
  let tagCount = 0;

  return menu.filter(item => {
    if (item.action !== 'tag' || tagCount < maxTags) {
      if (item.action === 'tag') tagCount += 1;
      return true;
    }
    return false;
  });
};
